import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import "../styles/WorkflowBuilder.css";

// ── Types ──────────────────────────────────────────────────────────────────────

type TriggerEvent =
  | "status_changed"
  | "application_created"
  | "interview_scheduled"
  | "no_response_days"
  | "offer_received"
  | "rejected";

type ActionType = "send_push" | "add_tag" | "add_note" | "move_status";

interface WorkflowAction {
  type: ActionType;
  title?: string;
  body?: string;
  url?: string;
  tag?: string;
  note?: string;
  status?: string;
}

interface TriggerFilter {
  toStatus?: string;
  fromStatus?: string;
  company?: string;
  hasTag?: string;
  daysSinceApplied?: number;
}

interface WorkflowRule {
  id: string;
  name: string;
  enabled: boolean;
  triggerEvent: TriggerEvent;
  triggerFilter: TriggerFilter;
  actions: WorkflowAction[];
  lastRunAt?: string;
  runCount: number;
  createdAt: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<TriggerEvent, string> = {
  status_changed:      "Status changes",
  application_created: "Application created",
  interview_scheduled: "Interview scheduled",
  no_response_days:    "No response after N days",
  offer_received:      "Offer received",
  rejected:            "Application rejected",
};

const ACTION_LABELS: Record<ActionType, string> = {
  send_push:   "Send push notification",
  add_tag:     "Add tag",
  add_note:    "Add note to application",
  move_status: "Change application status",
};

const STATUSES = [
  "applied", "interview_scheduled", "offered", "accepted", "rejected", "withdrawn",
];

const blankAction = (): WorkflowAction => ({ type: "send_push", title: "", body: "" });

const blankRule = (): Omit<WorkflowRule, "id" | "createdAt" | "runCount"> => ({
  name: "",
  enabled: true,
  triggerEvent: "status_changed",
  triggerFilter: {},
  actions: [blankAction()],
});

// ── Component ──────────────────────────────────────────────────────────────────

const WorkflowBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<WorkflowRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState<Partial<WorkflowRule> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/workflows");
      setRules(res.data);
    } catch {
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => setEditingRule({ ...blankRule() });
  const openEdit = (r: WorkflowRule) =>
    setEditingRule({ ...r, actions: r.actions.map(a => ({ ...a })) });
  const closeEditor = () => setEditingRule(null);

  const saveRule = async () => {
    if (!editingRule) return;
    if (!editingRule.name?.trim()) {
      toast.error("Rule name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingRule.id) {
        const res = await axiosInstance.patch(`/workflows/${editingRule.id}`, editingRule);
        setRules(prev => prev.map(r => r.id === editingRule.id ? res.data : r));
        toast.success("Workflow updated");
      } else {
        const res = await axiosInstance.post("/workflows", editingRule);
        setRules(prev => [...prev, res.data]);
        toast.success("Workflow created");
      }
      closeEditor();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (rule: WorkflowRule) => {
    try {
      const res = await axiosInstance.patch(`/workflows/${rule.id}`, { enabled: !rule.enabled });
      setRules(prev => prev.map(r => r.id === rule.id ? res.data : r));
    } catch {
      toast.error("Failed to update workflow");
    }
  };

  const deleteRule = async (id: string) => {
    if (!window.confirm("Delete this workflow rule?")) return;
    try {
      await axiosInstance.delete(`/workflows/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success("Workflow deleted");
    } catch {
      toast.error("Failed to delete workflow");
    }
  };

  const updateField = (field: string, value: any) =>
    setEditingRule(prev => prev ? { ...prev, [field]: value } : prev);

  const updateFilter = (field: string, value: any) =>
    setEditingRule(prev => {
      if (!prev) return prev;
      const filter: any = { ...(prev.triggerFilter || {}) };
      if (value === "" || value === undefined) {
        delete filter[field];
      } else {
        filter[field] = value;
      }
      return { ...prev, triggerFilter: filter };
    });

  const updateAction = (idx: number, field: string, value: any) =>
    setEditingRule(prev => {
      if (!prev) return prev;
      const actions = [...(prev.actions || [])];
      actions[idx] = { ...actions[idx], [field]: value };
      return { ...prev, actions };
    });

  const addAction = () =>
    setEditingRule(prev =>
      prev ? { ...prev, actions: [...(prev.actions || []), blankAction()] } : prev
    );

  const removeAction = (idx: number) =>
    setEditingRule(prev => {
      if (!prev) return prev;
      const actions = (prev.actions || []).filter((_, i) => i !== idx);
      return { ...prev, actions: actions.length ? actions : [blankAction()] };
    });

  if (loading) {
    return (
      <div className="wb-container">
        <div className="wb-loading">
          <div className="wb-spinner" />
          <p>Loading workflows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wb-container">
      <div className="wb-header">
        <button onClick={() => navigate("/dashboard")} className="wb-back-btn">
          &larr; Back
        </button>
        <div className="wb-header-text">
          <h1>Workflow Automation</h1>
          <p>Create if/then rules that run automatically on your applications</p>
        </div>
        <button className="wb-add-btn" onClick={openNew}>
          + New Rule
        </button>
      </div>

      {rules.length === 0 && !editingRule && (
        <div className="wb-empty">
          <div className="wb-empty-icon">⚡</div>
          <h3>No workflow rules yet</h3>
          <p>
            Automate repetitive tasks — like sending yourself a push notification
            when you get an interview, or tagging applications automatically.
          </p>
          <button className="wb-add-btn" onClick={openNew}>
            Create your first rule
          </button>
        </div>
      )}

      {rules.length > 0 && (
        <div className="wb-rule-list">
          {rules.map(rule => (
            <div key={rule.id} className={`wb-rule-card${rule.enabled ? "" : " wb-disabled"}`}>
              <div className="wb-rule-top">
                <div className="wb-rule-info">
                  <span className="wb-rule-name">{rule.name}</span>
                  <span className="wb-rule-trigger">
                    When: <strong>{TRIGGER_LABELS[rule.triggerEvent]}</strong>
                  </span>
                  <span className="wb-rule-actions-summary">
                    Then: {rule.actions.map(a => ACTION_LABELS[a.type]).join(", ")}
                  </span>
                </div>
                <div className="wb-rule-controls">
                  <label className="wb-toggle" title={rule.enabled ? "Enabled" : "Disabled"}>
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={() => toggleEnabled(rule)}
                    />
                    <span className="wb-toggle-slider" />
                  </label>
                  <button className="wb-icon-btn" onClick={() => openEdit(rule)} title="Edit">
                    ✏️
                  </button>
                  <button
                    className="wb-icon-btn wb-delete-btn"
                    onClick={() => deleteRule(rule.id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              {rule.runCount > 0 && (
                <div className="wb-rule-stats">
                  Ran {rule.runCount} time{rule.runCount !== 1 ? "s" : ""}
                  {rule.lastRunAt &&
                    ` · last ${new Date(rule.lastRunAt).toLocaleDateString()}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editingRule && (
        <div className="wb-modal-overlay" onClick={closeEditor}>
          <div className="wb-modal" onClick={e => e.stopPropagation()}>
            <div className="wb-modal-header">
              <h2>{editingRule.id ? "Edit Rule" : "New Rule"}</h2>
              <button className="wb-close-btn" onClick={closeEditor}>
                ✕
              </button>
            </div>

            <div className="wb-modal-body">
              <div className="wb-field">
                <label>Rule name</label>
                <input
                  type="text"
                  value={editingRule.name || ""}
                  onChange={e => updateField("name", e.target.value)}
                  placeholder="e.g. Notify me on interview"
                  maxLength={100}
                />
              </div>

              <div className="wb-section-title">IF (trigger)</div>
              <div className="wb-field">
                <label>Event</label>
                <select
                  value={editingRule.triggerEvent || "status_changed"}
                  onChange={e => updateField("triggerEvent", e.target.value)}
                >
                  {Object.entries(TRIGGER_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>

              <div className="wb-field wb-filter-group">
                <label>Filters (optional)</label>
                {editingRule.triggerEvent === "status_changed" && (
                  <>
                    <div className="wb-filter-row">
                      <span>To status</span>
                      <select
                        value={editingRule.triggerFilter?.toStatus || ""}
                        onChange={e => updateFilter("toStatus", e.target.value)}
                      >
                        <option value="">Any</option>
                        {STATUSES.map(s => (
                          <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                        ))}
                      </select>
                    </div>
                    <div className="wb-filter-row">
                      <span>From status</span>
                      <select
                        value={editingRule.triggerFilter?.fromStatus || ""}
                        onChange={e => updateFilter("fromStatus", e.target.value)}
                      >
                        <option value="">Any</option>
                        {STATUSES.map(s => (
                          <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                {editingRule.triggerEvent === "no_response_days" && (
                  <div className="wb-filter-row">
                    <span>Days since applied</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={editingRule.triggerFilter?.daysSinceApplied || 30}
                      onChange={e =>
                        updateFilter("daysSinceApplied", parseInt(e.target.value))
                      }
                    />
                  </div>
                )}
                <div className="wb-filter-row">
                  <span>Company contains</span>
                  <input
                    type="text"
                    value={editingRule.triggerFilter?.company || ""}
                    onChange={e => updateFilter("company", e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div className="wb-filter-row">
                  <span>Has tag</span>
                  <input
                    type="text"
                    value={editingRule.triggerFilter?.hasTag || ""}
                    onChange={e => updateFilter("hasTag", e.target.value)}
                    placeholder="e.g. industry:tech"
                  />
                </div>
              </div>

              <div className="wb-section-title">THEN (actions)</div>
              {(editingRule.actions || []).map((action, idx) => (
                <div key={idx} className="wb-action-block">
                  <div className="wb-action-header">
                    <select
                      value={action.type}
                      onChange={e => updateAction(idx, "type", e.target.value)}
                    >
                      {Object.entries(ACTION_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                    {(editingRule.actions || []).length > 1 && (
                      <button
                        className="wb-remove-action"
                        onClick={() => removeAction(idx)}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {action.type === "send_push" && (
                    <>
                      <input
                        type="text"
                        value={action.title || ""}
                        onChange={e => updateAction(idx, "title", e.target.value)}
                        placeholder="Notification title"
                      />
                      <input
                        type="text"
                        value={action.body || ""}
                        onChange={e => updateAction(idx, "body", e.target.value)}
                        placeholder="Notification body"
                      />
                      <input
                        type="text"
                        value={action.url || ""}
                        onChange={e => updateAction(idx, "url", e.target.value)}
                        placeholder="URL (optional, e.g. /applications)"
                      />
                    </>
                  )}

                  {action.type === "add_tag" && (
                    <input
                      type="text"
                      value={action.tag || ""}
                      onChange={e => updateAction(idx, "tag", e.target.value)}
                      placeholder="Tag (e.g. industry:tech)"
                    />
                  )}

                  {action.type === "add_note" && (
                    <textarea
                      value={action.note || ""}
                      onChange={e => updateAction(idx, "note", e.target.value)}
                      placeholder="Note text"
                      rows={3}
                    />
                  )}

                  {action.type === "move_status" && (
                    <select
                      value={action.status || ""}
                      onChange={e => updateAction(idx, "status", e.target.value)}
                    >
                      <option value="">Select status...</option>
                      {STATUSES.map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}

              <button className="wb-add-action-btn" onClick={addAction}>
                + Add another action
              </button>
            </div>

            <div className="wb-modal-footer">
              <label className="wb-enabled-toggle">
                <input
                  type="checkbox"
                  checked={editingRule.enabled !== false}
                  onChange={e => updateField("enabled", e.target.checked)}
                />
                Enabled
              </label>
              <div className="wb-footer-actions">
                <button className="wb-cancel-btn" onClick={closeEditor}>
                  Cancel
                </button>
                <button
                  className="wb-save-btn"
                  onClick={saveRule}
                  disabled={saving}
                >
                  {saving ? "Saving..." : editingRule.id ? "Update" : "Create Rule"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowBuilder;
